import { fabric } from 'fabric';
import { Group } from 'fabric/fabric-impl';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import alignment from 'modules/overlay-canvas/addons/alignment/alignment';
import history from 'modules/overlay-canvas/addons/history';
import { createIndicatorsRow, OrientationEnum, ShapeEnum } from 'modules/overlay-canvas/addons/placeholders/complex/indicators-row';
import {
  DEFAULT_SHOOTOUT_COLORS,
  getShootoutColors,
  paintShootoutIndicators,
  previewShootoutAttempts,
  ShootoutColors,
} from 'modules/overlay-canvas/addons/placeholders/complex/shootout-indicators';
import SnapToGrid from 'modules/overlay-canvas/addons/snap-to-grid';
import UDText from 'ud-ui/components/text';
import { round } from 'shared/utils/round';
import { selectFabricManager } from 'modules/template-editor/store/editor/selectors';
import ColorPicker from 'modules/template-editor/ui/components/elements/color-picker';
import NumberInput from 'modules/template-editor/ui/components/elements/number-input';
import ChoosingOrientation from './components/choosing-orientation';
import ChoosingShape from './components/choosing-shape';
import ChoosingTeam from './components/choosing-team';
import LayerControls from './components/layer-controls';
import TransformSection from './shared/transform-section';
import { useLiveTransform } from './shared/use-live-transform';
import LockPosition from './components/lock-position';
import { Item, LongDivider, ShortDivider } from './styles';
import { theme } from 'styles/theme/theme';

interface IProps {
  object: Group;
}

function ComplexPlaceholder(props: IProps) {
  const { t } = useLingui();
  const manager = useSelector(selectFabricManager);
  const { object } = props;

  const data = useMemo(() => {
    return object.data;
  }, [object.data]);
  const name = useMemo(() => {
    return data?.name ?? data?.indicator;
  }, [data?.indicator, data?.name]);
  const lockMovementX = object?.lockMovementX;

  const [hasTeam, setHasTeam] = useState(data?.hasTeam ?? data?.teamIndex !== undefined);
  // Расположение и размер читаются с холста на каждый кадр перетаскивания —
  // раньше они снимались один раз на выделение и «отставали» до переклика.
  const { position, size, setPosition, setSize } = useLiveTransform(object, manager?.canvas);
  const [teamIndex, setTeamIndex] = useState(data?.team ?? data?.teamIndex);
  const [backgroundColor, setBackgroundColor] = useState(data?.backgroundColor ?? '#FFFFFF00');
  const [elementColor, setElementColor] = useState(data?.elementColor ?? theme.colors.SurfaceRaisinBlack50);
  const [borderColor, setBorderColor] = useState(data?.borderColor ?? '#404861');
  const [textColor, setTextColor] = useState(data?.textColor ?? theme.colors.SurfaceWhite);
  const [orientation, setOrientation] = useState(data?.orientation ?? OrientationEnum.HORIZONTAL);
  const [isPositionLock, setIsPositionLock] = useState(lockMovementX ?? false);
  const [maxCount, setMaxCount] = useState(data?.maxCount ?? 5);
  const [spacing, setSpacing] = useState(data?.spacing ?? 10);
  const [shape, setShape] = useState(data?.shape ?? ShapeEnum.CIRCLE);
  const [shootoutColors, setShootoutColors] = useState<ShootoutColors>(getShootoutColors(data));

  useEffect(() => {
    if (name === 'teamComposition') {
      object.setControlsVisibility({
        mt: false,
        mb: false,
        ml: false,
        mr: false,
        bl: true,
        br: true,
        tl: true,
        tr: true,
      });
    }

    object.setControlsVisibility({
      mtr: false,
    });

    setIsPositionLock(lockMovementX ?? false);
  }, [lockMovementX, name, object]);

  useEffect(() => {
    if (!manager) return;
    manager.canvas.uniScaleKey = undefined;

    return () => {
      manager.canvas.uniScaleKey = 'shiftKey';
    };
  }, [manager]);

  useEffect(() => {
    setHasTeam(data?.hasTeam ?? data?.teamIndex !== undefined);
    if (data?.hasTeam) setTeamIndex(data?.team);
    if (data?.orientation) setOrientation(data.orientation);
    if (data?.maxCount) {
      setMaxCount(data.maxCount);
    }
    if (data?.spacing) setSpacing(data.spacing);
    if (data?.shape) setShape(data.shape);
    setShootoutColors(getShootoutColors(data));
  }, [data, data?.hasTeam, data?.team, data?.teamIndex, data?.orientation, data?.maxCount, data?.spacing, data?.shape]);

  const onFocusField = useCallback(() => {
    manager?.render();
    history.saveState();
  }, [manager]);

  const onChangePosition = useCallback(
    (x: number, y: number) => {
      object.setPositionByOrigin(new fabric.Point(x, y), 'left', 'top');
      setPosition({ x: round(x), y: round(y) });
      manager?.render();
    },
    [manager, object, setPosition],
  );

  const onChangeSize = useCallback(
    (width: number, height: number) => {
      object.set({ scaleX: width / (object?.width ?? 1), scaleY: height / (object?.height ?? 1) });
      setSize({ width: round(width), height: round(height) });
      SnapToGrid.transformAfterScaling(object);
      manager?.render();
    },
    [manager, object, setSize],
  );

  const onChangeTeam = useCallback(
    (teamIndex: 0 | 1) => {
      const oldTeamIndex = object.data?.teamIndex;
      if (oldTeamIndex !== undefined) {
        object.data = { ...object.data, teamIndex };
      } else if (!object.data?.team === undefined) {
        object.data = { ...object.data, team: teamIndex };
      }

      history.saveState();
      setTeamIndex(teamIndex);
    },
    [object],
  );

  const changeBackgroundColor = useCallback(
    (color: string) => {
      setBackgroundColor(color);
      object.data = { ...object.data, backgroundColor: color };
      const background = object.getObjects('rect')[0];
      if (background) {
        background.set({ fill: color });
      }
      manager?.render();
      history.saveState();
    },
    [manager, object],
  );

  const changeElementColor = useCallback(
    (color: string) => {
      setElementColor(color);
      object.data = { ...object.data, elementColor: color };

      switch (object.data?.indicator) {
        case 'teamComposition':
          const elements = object.getObjects('group') as Group[];
          if (elements.length > 0) {
            for (const element of elements) {
              const subElements = element.getObjects('group') as Group[];
              if (subElements.length > 0) {
                for (const subElement of subElements) {
                  const rect = subElement.getObjects('rect')[0];
                  if (rect) {
                    rect.set({ fill: color });
                  }
                }
              }
            }
          }
          break;
        case 'teamPeriodFouls':
          // Обновление цвета кругов/квадратов фолов
          const indicators = object.getObjects().filter((obj) => obj.type === 'circle' || obj.type === 'rect');

          if (indicators.length > 0) {
            for (const indicator of indicators) {
              if (indicator !== object.getObjects('rect')[0]) {
                // Не обновляем фон
                indicator.set({ fill: color });
              }
            }
          }
          break;
        default:
          break;
      }
      // Добавляем вызов transformAfterScaling для индикатора teamPeriodFouls
      if (object.data?.indicator === 'teamPeriodFouls') {
        SnapToGrid.transformAfterScaling(object);
      }
      manager?.render();
      history.saveState();
    },
    [manager, object],
  );

  const changeBorderColor = useCallback(
    (color: string) => {
      setBorderColor(color);
      object.data = { ...object.data, borderColor: color };
      const elements = object.getObjects('group') as Group[];
      if (elements.length > 0) {
        for (const element of elements) {
          const subElements = element.getObjects('group') as Group[];
          if (subElements.length > 0) {
            for (const subElement of subElements) {
              const rect = subElement.getObjects('rect')[0];
              if (rect) {
                rect.set({ stroke: color });
              }
            }
          }
        }
      }
      manager?.render();
      history.saveState();
    },
    [manager, object],
  );

  const changeTextColor = useCallback(
    (color: string) => {
      setTextColor(color);
      object.data = { ...object.data, textColor: color };
      const elements = object.getObjects('group') as Group[];
      if (elements.length > 0) {
        for (const element of elements) {
          const subElements = element.getObjects('group') as Group[];
          if (subElements.length > 0) {
            for (const subElement of subElements) {
              const text = subElement.getObjects('text')[0];
              if (text) {
                text.set({ fill: color });
              }
            }
          }
        }
      }
      manager?.render();
      history.saveState();
    },
    [manager, object],
  );

  /**
   * Ряд индикаторов пересобирается целиком на любое изменение: количество, отступ,
   * форма и ориентация задают одну и ту же раскладку, и менять её по месту дороже,
   * чем построить заново.
   */
  const rebuildIndicators = useCallback(
    (patch: Partial<{ maxCount: number; spacing: number; shape: ShapeEnum; orientation: OrientationEnum }>) => {
      if (!manager || !object) return;

      object.data = { ...object.data, ...patch };

      const background = object.getObjects('rect')[0];
      const indicators = object.getObjects().filter((obj) => (obj.type === 'circle' || obj.type === 'rect') && obj !== background);
      indicators.forEach((indicator) => object.remove(indicator));

      const isShootoutSeries = object.data?.indicator === 'teamShootoutSeries';
      const newIndicators = createIndicatorsRow({
        maxCount: object.data.maxCount || 5,
        color: isShootoutSeries
          ? object.data.pendingColor || DEFAULT_SHOOTOUT_COLORS.pendingColor
          : object.data.elementColor || '#FFA500',
        spacing: object.data.spacing ?? 10,
        orientation: object.data.orientation || OrientationEnum.HORIZONTAL,
        shape: object.data.shape || ShapeEnum.CIRCLE,
        width: background.getScaledWidth(),
        height: background.getScaledHeight(),
      });
      if (isShootoutSeries) {
        paintShootoutIndicators(newIndicators, previewShootoutAttempts(object.data.maxCount || 5), getShootoutColors(object.data));
      }

      newIndicators.forEach((indicator) => object.add(indicator));

      object.setCoords();
      SnapToGrid.transformAfterScaling(object);
      manager.render();
      history.saveState();
    },
    [manager, object],
  );

  const changeMaxCount = useCallback(
    (newMaxCount: number) => {
      const clamped = Math.min(20, Math.max(1, newMaxCount));
      setMaxCount(clamped);
      rebuildIndicators({ maxCount: clamped });
    },
    [rebuildIndicators],
  );

  const changeSpacing = useCallback(
    (newSpacing: number) => {
      const clamped = Math.max(0, Math.round(newSpacing));
      setSpacing(clamped);
      rebuildIndicators({ spacing: clamped });
    },
    [rebuildIndicators],
  );

  const changeShape = useCallback(
    (newShape: ShapeEnum) => {
      setShape(newShape);
      rebuildIndicators({ shape: newShape });
    },
    [rebuildIndicators],
  );

  const changeOrientation = useCallback(
    (newOrientation: OrientationEnum) => {
      if (newOrientation === orientation) return;
      setOrientation(newOrientation);
      rebuildIndicators({ orientation: newOrientation });
    },
    [orientation, rebuildIndicators],
  );

  /**
   * Цвета исходов серии видны сразу: в редакторе ряд показан образцовой серией,
   * иначе два цвета из трёх оператор выбирал бы вслепую.
   */
  const changeShootoutColor = useCallback(
    (key: keyof ShootoutColors, color: string) => {
      if (!manager || !object) return;

      object.data = { ...object.data, [key]: color };
      setShootoutColors(getShootoutColors(object.data));

      const background = object.getObjects('rect')[0];
      const indicators = object.getObjects().filter((obj) => (obj.type === 'circle' || obj.type === 'rect') && obj !== background);
      paintShootoutIndicators(indicators, previewShootoutAttempts(object.data.maxCount || 5), getShootoutColors(object.data));

      manager.render();
      history.saveState();
    },
    [manager, object],
  );

  const showDebugInfo = true;
  const isDev = useMemo(() => {
    return process.env.NODE_ENV === 'development';
  }, []);

  const onClickAlignmentCenterVertical = () => {
    const newPos = alignment.single.centerVertical(object);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const onClickAlignmentCenterHorizontal = () => {
    const newPos = alignment.single.centerHorizontal(object);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const categoryName = useMemo(() => {
    switch (name) {
      case 'teamComposition':
        return t`Состав команды`;
      case 'teamPeriodFouls':
        return t`Фолы команды в периоде`;
      case 'teamShootoutSeries':
        return t`Серия пенальти`;
      default:
        return t`Плейсхолдер`;
    }
  }, [name, t]);

  /** Ряд индикаторов настраивается одинаково у фолов в тайме и у серии пенальти. */
  const isIndicatorsRow = name === 'teamPeriodFouls' || name === 'teamShootoutSeries';

  return (
    <>
      <Item>
        <div>{categoryName}</div>
      </Item>
      <ShortDivider />
      <TransformSection
        position={position}
        size={size}
        onChangePosition={onChangePosition}
        onChangeSize={onChangeSize}
        onAlignCenterHorizontal={onClickAlignmentCenterHorizontal}
        onAlignCenterVertical={onClickAlignmentCenterVertical}
        onFocusField={onFocusField}
      />
      {hasTeam && (
        <>
          <LongDivider />
          <Item>
            <ChoosingTeam onChange={onChangeTeam} value={teamIndex} />
          </Item>
        </>
      )}
      {isIndicatorsRow && (
        <>
          <LongDivider />
          <Item>
            <ChoosingOrientation onChange={changeOrientation} value={orientation} />
          </Item>
          <LongDivider />
          <Item>
            <UDText type={'caption-1'}>{t`Максимальное кол-во индикаторов`}</UDText>
            <NumberInput
              value={maxCount}
              onChange={changeMaxCount}
              onFocus={onFocusField}
              min={1}
              max={20}
              step={1}
              precision={0}
              style={{ marginBottom: 10 }}
            />

            <UDText type={'caption-1'}>{t`Расстояние между индикаторами (px)`}</UDText>
            <NumberInput
              value={spacing}
              onChange={changeSpacing}
              onFocus={onFocusField}
              min={0}
              step={5}
              precision={0}
              style={{ marginBottom: 10 }}
            />

            <ChoosingShape onChange={changeShape} value={shape} />
          </Item>
        </>
      )}
      {(name === 'teamComposition' || name === 'teamPeriodFouls') && (
        <>
          <LongDivider />
          <Item>
            <UDText type={'caption-1'}>{t`Цвет элемента`}</UDText>
            <ColorPicker color={elementColor} onChange={changeElementColor} />
            {name === 'teamComposition' && (
              <>
                <UDText type={'caption-1'}>{t`Цвет фона`}</UDText>
                <ColorPicker color={backgroundColor} onChange={changeBackgroundColor} />
                <UDText type={'caption-1'}>{t`Цвет рамки`}</UDText>
                <ColorPicker color={borderColor} onChange={changeBorderColor} />
                <UDText type={'caption-1'}>{t`Цвет текста`}</UDText>
                <ColorPicker color={textColor} onChange={changeTextColor} />
              </>
            )}
          </Item>
        </>
      )}
      {name === 'teamShootoutSeries' && (
        <>
          <LongDivider />
          <Item>
            <UDText type={'caption-1'}>{t`Цвет забитого пенальти`}</UDText>
            <ColorPicker color={shootoutColors.goalColor} onChange={(color) => changeShootoutColor('goalColor', color)} />
            <UDText type={'caption-1'}>{t`Цвет промаха`}</UDText>
            <ColorPicker color={shootoutColors.missColor} onChange={(color) => changeShootoutColor('missColor', color)} />
            <UDText type={'caption-1'}>{t`Цвет непробитого удара`}</UDText>
            <ColorPicker color={shootoutColors.pendingColor} onChange={(color) => changeShootoutColor('pendingColor', color)} />
          </Item>
        </>
      )}
      <LongDivider />
      <LayerControls object={object} />
      <LockPosition objects={[object]} onChange={setIsPositionLock} value={isPositionLock} />
      {isDev && showDebugInfo && (
        <Item>
          hasTeam: {hasTeam ? 'true' : 'false'},
          <br />
          data: <span style={{ whiteSpace: 'pre' }}>{JSON.stringify(object.data, null, 2)}</span>
        </Item>
      )}
    </>
  );
}

export default ComplexPlaceholder;
